<template>
  <div
    class="sticky-cta-footer js-mobile-sticky-bar"
    :class="`sticky-cta-footer--${pageType}`"
  >
    <div class="sticky-cta-footer__content">
      <a
        class="sticky-cta-footer__cta"
        data-cta-location="sticky-footer-listing"
        data-gtm-category="CTA"
        data-gtm-event="cta_click"
        v-if="listingFooter"
        :data-gtm-label="gtmLabel"
        :href="listingUrl"
      >
        {{ ctaLabel }}
      </a>
      <button
        class="sticky-cta-footer__cta js-toggle-global-contact-form"
        data-location="sticky-footer"
        type="button"
        v-else
      >
        {{ ctaLabel }}
      </button>

      <!-- The line carries markup of our own — a bold value, or the lawyer
      listing link — placed inside a translated string. -->
      <p class="sticky-cta-footer__meta" v-if="meta" v-html="meta"></p>

      <a
        class="sticky-cta-footer__advisors"
        data-cta-location="sticky-footer-advisors"
        data-gtm-category="CTA"
        data-gtm-event="cta_click"
        v-if="advisors.length"
        :data-gtm-label="`Mobile - sticky footer - faces (${pageType})`"
        :href="listingUrl"
      >
        <span class="screen-reader-text">{{ listingLabel }}</span>
        <ix-img
          alt=""
          class="sticky-cta-footer__advisor"
          loading="lazy"
          v-for="advisor in advisors"
          :key="advisor"
          :height="advisorSize"
          :imgix-params="imgixParams"
          :src="advisor"
          :width="advisorSize"
        />
      </a>
    </div>
  </div>
</template>

<script>
  import { ROLE } from "Composables/useRoleTermLocalStorage";
  import { sprintf } from "sprintf-js";
  import {
    loadAvailability,
    readCachedAvailability,
    whenIdle,
  } from "../availability";

  // The page types whose button leads to a listing of advisors, with the label
  // it carries and the role whose availability the line reports. Law is absent:
  // its button opens the contact form, and it promises no time.
  const LISTING_FOOTERS = {
    therapy: { label: "showTherapists", role: ROLE.THERAPY },
  };
  // Therapy clusters its three faces in a triangle; law lines
  // them up in a row, which is a face shorter and so has room for a larger
  // one. Both sizes live in _sticky-cta-footer.scss too.
  const ADVISOR_SIZE = 24;
  const ADVISOR_SIZE_JURIST = 30;

  export default {
    beforeUnmount() {
      window.removeEventListener(
        "city-storage-updated-event",
        this.refreshAvailability
      );
    },

    computed: {
      ctaLabel() {
        return this.listingFooter
          ? this.texts[this.listingFooter.label]
          : this.texts.getContacted;
      },

      // Named for the page type rather than the label the button carries, so a
      // report keeps reading the same after a copy change.
      gtmLabel() {
        return `Mobile - sticky footer - listing (${this.pageType})`;
      },

      listingFooter() {
        return LISTING_FOOTERS[this.pageType] || null;
      },

      // The faces carry no alt text of their own, so the link around them
      // gets its name from the listing it shares with the action beside it.
      listingLabel() {
        return this.listingFooter ? this.ctaLabel : this.texts.chooseLawyer;
      },

      // The time and the city it belongs to as one wrapping sentence.
      meta() {
        if (!this.listingFooter) {
          // The attributes ride along in the string's own markup: the click is
          // delegated, so the anchor only has to carry them — see
          // Utils/click-tracking.
          return sprintf(
            this.texts.orChooseLawyer,
            `<a data-cta-location="sticky-footer-listing" data-gtm-category="CTA" data-gtm-event="cta_click" data-gtm-label="${this.gtmLabel}" href="${this.listingUrl}">${this.texts.chooseLawyer}</a>`
          );
        }

        return [
          this.availability.time
            ? sprintf(
                this.texts.nextTime,
                `<strong>${this.availability.time}</strong>`
              )
            : "",
          this.availability.city
            ? sprintf(
                this.texts.inCity,
                `<strong>${this.availability.city}</strong>`
              )
            : "",
        ]
          .filter(Boolean)
          .join(" ");
      },
    },

    data() {
      const { pageType, listingUrl, advisors } = this.$mountEl.dataset;

      // The cache is what the bar paints with before any request answers, and
      // mounted() refreshes it once it has gone stale.
      const listingFooter = LISTING_FOOTERS[pageType] || null;
      const cached = listingFooter
        ? readCachedAvailability(listingFooter.role)
        : null;

      const advisorSize =
        pageType === "jurist" ? ADVISOR_SIZE_JURIST : ADVISOR_SIZE;

      return {
        advisorSize,
        // Set on the global settings options page, so they are here on first
        // paint rather than arriving with the page's advisors.
        advisors: JSON.parse(advisors || "[]"),
        availability: cached || { city: "", time: "" },
        // Twice the rendered size, for the same crop on a retina screen.
        imgixParams: {
          crop: "faces",
          fit: "crop",
          h: advisorSize * 2,
          w: advisorSize * 2,
        },
        listingUrl,
        pageType,
        texts: window.theme_vars.stickyCtaFooter,
      };
    },

    methods: {
      // Empty is an answer too: with no bookable slot anywhere on the listing
      // the bar drops the line rather than keeping a stale promise. A failed
      // request is not, and leaves what is already there.
      async refreshAvailability() {
        const availability = await loadAvailability(this.listingFooter.role);

        if (availability) {
          this.availability = availability;
        }
      },
    },

    mounted() {
      if (!this.listingFooter) {
        return;
      }

      whenIdle(() => this.refreshAvailability());
      // The city is the visitor's to change while they are here, and it picks
      // the advisors — so it picks the time.
      window.addEventListener(
        "city-storage-updated-event",
        this.refreshAvailability
      );
    },

    name: "StickyCtaFooter",
  };
</script>
